import React, { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TaskProvider, useTask } from './contexts/TaskContext';
import Header from './components/Header';
import TaskColumn from './components/TaskColumn';
import FinishDayModal from './components/FinishDayModal';
import RecurringTaskModal from './components/RecurringTaskModal';
import LoginScreen from './components/LoginScreen';
import ShoppingList from './components/ShoppingList';
import BucketListView from './components/BucketListView';
import ChecklistView from './components/ChecklistView';
import AchievementPopup from './components/AchievementPopup';
import './styles/App.css';

const APP_VERSION = 'v1.18.1';

// モバイル用アイコンナビのページ定義（上部アイコン → 全画面ページ切り替え）
const NAV_PAGES = [
  { key: 'today', icon: '📋', label: '今日', color: 'green' },
  { key: 'future', icon: '📅', label: '明日以降', color: 'orange' },
  { key: 'shopping', icon: '🛒', label: '買い物', color: 'blue' },
  { key: 'checklist', icon: '🧳', label: '旅行', color: 'teal' },
  { key: 'bucket', icon: '🎯', label: 'バケット', color: 'purple' },
];

// PC用タブ定義（メイン＝3カラム同時表示／買い物・チェックリストは別タブ）
const DESKTOP_TABS = [
  { key: 'main', icon: '📋', label: 'メイン', color: 'green' },
  { key: 'shopping', icon: '🛒', label: '買い物', color: 'blue' },
  { key: 'checklist', icon: '🧳', label: '旅行リスト', color: 'teal' },
];

const VALID_PAGE_KEYS = NAV_PAGES.map((p) => p.key);
const VALID_DESKTOP_KEYS = DESKTOP_TABS.map((p) => p.key);

// 画面幅769px以上をPCとみなす（CSSの768pxブレークポイントと整合）
function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(
    () => window.matchMedia('(min-width: 769px)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 769px)');
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isDesktop;
}

// リスト間移動の「元に戻す」トースト（移動後3秒間表示）
function MoveUndoToast() {
  const { moveToast, undoMove } = useTask();
  if (!moveToast) return null;
  return (
    <div className="undo-toast" role="status">
      <span className="undo-toast-text">{moveToast.toLabel}に移動しました</span>
      <button type="button" className="undo-toast-btn" onClick={undoMove}>
        元に戻す
      </button>
    </div>
  );
}

function AppContent() {
  const { user, loading } = useAuth();
  const isDesktop = useIsDesktop();
  const [showFinishModal, setShowFinishModal] = useState(false);
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  // モバイル：アクティブページ（最後に開いたページを記憶）
  const [activePage, setActivePage] = useState(() => {
    const saved = localStorage.getItem('tf_activePage');
    return VALID_PAGE_KEYS.includes(saved) ? saved : 'today';
  });
  // PC：アクティブタブ（最後に開いたタブを記憶）
  const [activeDesktopTab, setActiveDesktopTab] = useState(() => {
    const saved = localStorage.getItem('tf_activeDesktopTab');
    return VALID_DESKTOP_KEYS.includes(saved) ? saved : 'main';
  });

  useEffect(() => {
    localStorage.setItem('tf_activePage', activePage);
  }, [activePage]);

  useEffect(() => {
    localStorage.setItem('tf_activeDesktopTab', activeDesktopTab);
  }, [activeDesktopTab]);

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
        <p>読み込み中...</p>
      </div>
    );
  }

  if (!user) {
    return <LoginScreen />;
  }

  // 「今日のタスクを終了する」バーの表示条件
  // PC：メインタブ（3カラムに「今日」を含む）／モバイル：今日・明日以降ページ
  const showFinishBar = isDesktop
    ? activeDesktopTab === 'main'
    : activePage === 'today' || activePage === 'future';

  // ナビ：PC＝3タブ／モバイル＝5アイコン（見た目は共通の icon-nav）
  const navTabs = isDesktop ? DESKTOP_TABS : NAV_PAGES;
  const navActiveKey = isDesktop ? activeDesktopTab : activePage;
  const setNavActiveKey = isDesktop ? setActiveDesktopTab : setActivePage;

  return (
    <TaskProvider>
      <div className="app">
        <Header version={APP_VERSION} />

        {/* アイコンナビ：上部にアイコンを表示し、押すとページ・タブが切り替わる */}
        <nav className="icon-nav" aria-label="ページ切り替え">
          {navTabs.map((p) => (
            <button
              key={p.key}
              type="button"
              className={`icon-nav-btn nav-${p.color} ${navActiveKey === p.key ? 'active' : ''}`}
              onClick={() => setNavActiveKey(p.key)}
              aria-current={navActiveKey === p.key ? 'page' : undefined}
            >
              <span className="icon-nav-icon" aria-hidden="true">{p.icon}</span>
              <span className="icon-nav-label">{p.label}</span>
            </button>
          ))}
        </nav>

        {isDesktop ? (
          // PC：メイン＝3カラム同時表示（バケット／今日／明日以降）、買い物・チェックリストは別タブ
          activeDesktopTab === 'main' ? (
            <main className="main-content desktop-columns">
              <BucketListView />
              <TaskColumn category="today" onOpenRecurring={() => setShowRecurringModal(true)} />
              <TaskColumn category="future" />
            </main>
          ) : (
            <main className="main-content page-content">
              {activeDesktopTab === 'shopping' && <ShoppingList />}
              {activeDesktopTab === 'checklist' && <ChecklistView />}
            </main>
          )
        ) : (
          // モバイル：全画面ページ（アクティブページのみ表示）
          <main className={`main-content page-content ${showFinishBar ? 'with-finish-bar' : ''}`}>
            {activePage === 'today' && (
              <TaskColumn category="today" onOpenRecurring={() => setShowRecurringModal(true)} />
            )}
            {activePage === 'future' && <TaskColumn category="future" />}
            {activePage === 'shopping' && <ShoppingList />}
            {activePage === 'checklist' && <ChecklistView />}
            {activePage === 'bucket' && <BucketListView />}
          </main>
        )}

        {/* 下部固定バー：今日のタスクを終了するボタン（今日・明日以降ページのみ） */}
        {showFinishBar && (
          <div className="finish-day-bar">
            <button
              className="btn btn-finish"
              onClick={() => setShowFinishModal(true)}
            >
              <span className="finish-icon">🌙</span>
              今日のタスクを終了する
            </button>
          </div>
        )}

        <FinishDayModal
          isOpen={showFinishModal}
          onClose={() => setShowFinishModal(false)}
        />

        {/* 定期タスク管理画面 */}
        <RecurringTaskModal
          isOpen={showRecurringModal}
          onClose={() => setShowRecurringModal(false)}
        />

        {/* リスト間移動の「元に戻す」トースト */}
        <MoveUndoToast />

        {/* バケットリスト達成おめでとうポップアップ（3秒表示） */}
        <AchievementPopup />
      </div>
    </TaskProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}
